/**
 * Centralized legal configuration for TRONA Platform
 * All legal document metadata, company info, and policy constants
 */

export const LEGAL_CONFIG = {
  company: {
    name: 'TRONA LLC',
    legalName: 'TRONA LLC',
    state: 'Wyoming',
    country: 'USA',
    address: 'TRONA registered address pending',
    foundedYear: 2026,
  },

  contact: {
    support: 'support@tronallc.com',
    privacy: 'privacy@tronallc.com',
    billing: 'billing@tronallc.com',
    legal: 'legal@tronallc.com',
    phone: null, // Add when available
  },

  urls: {
    website: 'https://tronallc.com',
    helpCenter: '/empresa/ayuda',
    terms: '/terms',
    privacy: '/privacy',
    cookies: '/cookies',
    refund: '/refund-policy',
    aup: '/acceptable-use',
  },

  documents: {
    terms: {
      version: '1.0',
      lastUpdated: '2025-01-15',
      effectiveDate: '2025-01-15',
    },
    privacy: {
      version: '1.0',
      lastUpdated: '2025-01-15',
      effectiveDate: '2025-01-15',
    },
    cookies: {
      version: '1.0',
      lastUpdated: '2025-01-15',
      effectiveDate: '2025-01-15',
    },
    refund: {
      version: '1.0',
      lastUpdated: '2025-01-15',
      effectiveDate: '2025-01-15',
    },
    aup: {
      version: '1.0',
      lastUpdated: '2025-01-15',
      effectiveDate: '2025-01-15',
    },
  },

  jurisdiction: {
    governingLaw: 'State of Wyoming, United States',
    venue: 'Laramie County, Wyoming',
    arbitration: 'American Arbitration Association (AAA)',
    arbitrationRules: 'Commercial Arbitration Rules',
    classActionWaiver: true,
  },

  dataRetention: {
    activeAccount: 'While account is active',
    postTermination: '7 years after account closure',
    formationDocuments: '7 years',
    financialRecords: '7 years',
    technicalLogs: '90 days',
    supportTickets: '2 years',
    analyticsData: '2 years',
  },

  cookies: {
    essential: [
      {
        name: 'auth0.*',
        purpose: 'Authentication and session management',
        duration: 'Session / 7 days',
        provider: 'Auth0',
      },
      {
        name: 'XSRF-TOKEN',
        purpose: 'Security - CSRF protection',
        duration: 'Session',
        provider: 'TRONA',
      },
      {
        name: '__cf_bm',
        purpose: 'Cloudflare bot management',
        duration: '30 minutes',
        provider: 'Cloudflare',
      },
    ],
    functional: [
      {
        name: 'trona_preferences',
        purpose: 'User preferences (language, theme)',
        duration: '1 year',
        provider: 'TRONA',
      },
      {
        name: 'trona_cookie_consent',
        purpose: 'Cookie consent preferences',
        duration: '1 year',
        provider: 'TRONA',
      },
    ],
    analytics: [
      // Add analytics cookies when implemented
    ],
  },

  plans: {
    business: {
      name: 'Business',
      price: 1100,
      currency: 'USD',
      description: 'LLC formation with EIN, base documents, banking support, and payment preparation',
    },
    custom: {
      name: 'A medida',
      price: null,
      currency: 'USD',
      description: 'Contact-only scope for complex formations',
    },
  },

  refundPolicy: {
    processingFee: 5,
    currency: 'USD',
    refundWindow: 'Before filing is submitted',
    noRefundAfter: 'Filing submission',
    governmentFeesRefundable: false,
    responseTime: '5 business days',
    processingTime: '5-10 business days',
  },

  financialServices: {
    // Important: TRONA provides APPLICATION ASSISTANCE, not account opening
    serviceType: 'Application Assistance',

    // Application fee for financial services assistance
    applicationFee: {
      amount: 50,
      currency: 'USD',
      description: {
        es: 'Tarifa de asistencia para aplicación',
        en: 'Application assistance fee',
      },
    },

    // Main disclaimer - shown in application modal
    disclaimer: {
      es: 'TRONA proporciona asistencia para aplicar a servicios financieros. La aprobación final depende exclusivamente del proveedor (banco, procesador de pagos, etc.). TRONA no garantiza la aprobación de ninguna solicitud.',
      en: 'TRONA provides assistance with applying to financial services. Final approval depends solely on the provider (bank, payment processor, etc.). TRONA does not guarantee approval of any application.',
    },

    // Banner disclaimer - shown at top of banking page
    bannerDisclaimer: {
      es: 'TRONA facilita tu acceso a servicios financieros mediante alianzas con proveedores de confianza. TRONA no es un banco ni institución financiera. La apertura de cuentas está sujeta a la aprobación de cada proveedor.',
      en: "TRONA facilitates your access to financial services through partnerships with trusted providers. TRONA is not a bank or financial institution. Account opening is subject to each provider's approval.",
    },

    // Affiliate disclosure
    affiliateDisclosure: {
      es: 'TRONA puede recibir una compensación de los proveedores de servicios financieros cuando abres una cuenta a través de nuestra plataforma. Esto no afecta el costo del servicio para ti.',
      en: 'TRONA may receive compensation from financial service providers when you open an account through our platform. This does not affect the cost of service to you.',
    },

    // Liability disclaimer
    liabilityDisclaimer: {
      es: 'TRONA no es responsable por las decisiones, políticas, términos o acciones de los proveedores de servicios financieros. Tu relación bancaria será directamente con el proveedor seleccionado.',
      en: 'TRONA is not responsible for the decisions, policies, terms, or actions of financial service providers. Your banking relationship will be directly with the selected provider.',
    },

    // Data sharing notice
    dataSharing: {
      es: 'Al solicitar un servicio financiero, autorizas a TRONA a compartir tu información empresarial con el proveedor seleccionado para procesar tu solicitud. Consulta nuestra Política de Privacidad para más detalles.',
      en: 'By applying for a financial service, you authorize TRONA to share your business information with the selected provider to process your application. See our Privacy Policy for details.',
    },

    // Eligibility notice
    eligibility: {
      es: 'Los requisitos de elegibilidad varían según el proveedor. Tener una LLC formada no garantiza la aprobación de servicios financieros. Cada proveedor realiza su propia evaluación.',
      en: 'Eligibility requirements vary by provider. Having a formed LLC does not guarantee approval for financial services. Each provider conducts their own evaluation.',
    },

    // Terms acceptance text
    termsAcceptance: {
      title: {
        es: 'Términos del Servicio de Asistencia Financiera',
        en: 'Financial Assistance Service Terms',
      },
      items: {
        es: [
          'TRONA proporciona asistencia para aplicar a servicios financieros, NO apertura directa de cuentas.',
          'La aprobación de cualquier servicio financiero depende exclusivamente del proveedor.',
          'TRONA no garantiza la aprobación de ninguna solicitud de cuenta bancaria o servicio financiero.',
          'Al continuar, autorizas a TRONA a compartir los datos de tu empresa con el proveedor seleccionado.',
          'TRONA puede recibir compensación de los proveedores cuando abres una cuenta.',
          'Tu relación contractual será directamente con el proveedor de servicios financieros, no con TRONA.',
        ],
        en: [
          'TRONA provides assistance with applying to financial services, NOT direct account opening.',
          'Approval of any financial service depends solely on the provider.',
          'TRONA does not guarantee approval of any bank account or financial service application.',
          'By continuing, you authorize TRONA to share your company data with the selected provider.',
          'TRONA may receive compensation from providers when you open an account.',
          'Your contractual relationship will be directly with the financial service provider, not with TRONA.',
        ],
      },
      checkbox: {
        es: 'He leído y acepto los términos del servicio de asistencia financiera de TRONA',
        en: "I have read and accept TRONA's financial assistance service terms",
      },
    },

    refundOnRejection: {
      percentage: 70,
      description: {
        es: 'Si tu solicitud es rechazada por el proveedor, recibirás un reembolso del 70% del monto pagado. El 30% restante cubre los costos administrativos de procesamiento y verificación.',
        en: 'If your application is rejected by the provider, you will receive a 70% refund of the amount paid. The remaining 30% covers administrative processing and verification costs.',
      },
      processingTime: '5-10 business days',
    },

    // For imported companies - previous application check
    previousApplicationCheck: {
      label: {
        es: 'Ya apliqué anteriormente a este servicio por mi cuenta',
        en: 'I have previously applied for this service on my own',
      },
      warning: {
        es: 'Si ya aplicaste antes, el proveedor puede rechazar una nueva solicitud. Te recomendamos contactar directamente al proveedor para dar seguimiento a tu solicitud existente.',
        en: 'If you have already applied, the provider may reject a new application. We recommend contacting the provider directly to follow up on your existing application.',
      },
    },

    providers: {
      banks: ['Proveedores bancarios'],
      paymentProcessors: ['Procesadores de pago'],
      cryptoExchanges: ['Exchanges de criptomonedas'],
    },
  },

  acceptableUse: {
    prohibitedActivities: [
      'Fraudulent or deceptive business practices',
      'Money laundering or terrorist financing',
      'Illegal activities under US federal or state law',
      'Impersonating another person or entity',
      'Creating shell companies for illegal purposes',
      'Evading taxes or regulatory requirements',
      'Violating intellectual property rights',
      'Harassment, abuse, or threatening behavior',
    ],
    contentRestrictions: [
      'Malware, viruses, or harmful code',
      'Spam or unsolicited commercial communications',
      'Content that violates third-party rights',
      'Illegal or prohibited content',
    ],
  },
};

/**
 * Format a date for display in legal documents
 * @param {string} dateString - ISO date string (YYYY-MM-DD)
 * @param {string} locale - Locale for formatting (default: 'es-ES')
 * @returns {string} Formatted date
 */
export const formatLegalDate = (dateString, locale = 'es-ES') => {
  const date = new Date(dateString);
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Get the last updated date for a legal document
 * @param {string} documentType - Type of document (terms, privacy, cookies, refund, aup)
 * @returns {string} Last updated date
 */
export const getDocumentLastUpdated = (documentType) => {
  return LEGAL_CONFIG.documents[documentType]?.lastUpdated || null;
};

/**
 * Get company contact email by type
 * @param {string} type - Contact type (support, privacy, billing, legal)
 * @returns {string} Email address
 */
export const getContactEmail = (type) => {
  return LEGAL_CONFIG.contact[type] || LEGAL_CONFIG.contact.support;
};

export default LEGAL_CONFIG;
